/**
 * Application context provider
 */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useClientData from '../hooks/useClientData';
import usePinnedClients from '../hooks/usePinnedClients';
import usePinnedListState from '../hooks/usePinnedListState';
import { processCountryData } from '../services/dataService';
import { loadSponsorData } from '../services/data-loading/sponsorDataLoader';
import { getConferenceMatchingClients, filterClientsByConference } from '../services/conferenceFilterService';
import { enrichClientsWithConferenceRevenue } from '../services/conferenceRevenueService';
import { YEAR_CONFIG } from '../config/yearConfig';

// Create context
const AppContext = createContext();

/**
 * Build normalized year boundary payload from YEAR_CONFIG.
 * @returns {{baseYear:number,lastDataYear:number,defaultAllYearsSortYear:number,comparisonAnchorYear:number,yearlyMetrics:string[],yearRange:number[]}}
 */
export const buildYearBoundariesFromConfig = () => {
  const baseYear = Number(YEAR_CONFIG.baseYear);
  const lastDataYear = Number(YEAR_CONFIG.lastDataYear);
  const defaultAllYearsSortYear = Number(YEAR_CONFIG.defaultAllYearsSortYear);
  const comparisonAnchorYear = Number(YEAR_CONFIG.comparisonAnchorYear);

  const yearRange = [];
  if (Number.isInteger(baseYear) && Number.isInteger(lastDataYear) && baseYear <= lastDataYear) {
    for (let year = baseYear; year <= lastDataYear; year++) {
      yearRange.push(year);
    }
  }

  return {
    baseYear,
    lastDataYear,
    defaultAllYearsSortYear,
    comparisonAnchorYear,
    yearlyMetrics: Array.isArray(YEAR_CONFIG.yearlyMetrics) ? [...YEAR_CONFIG.yearlyMetrics] : [],
    yearRange
  };
};

/**
 * App context provider
 * @param {Object} props - Component props
 * @returns {JSX.Element} Provider component
 */
export const AppContextProvider = ({ children }) => {
  // Loading state for manager selection
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  
  // State for tier coloring on map
  const [useTierColors, setUseTierColors] = useState(false);
  
  // State for marker style (true = new pie chart design, false = old circle design)
  const [usePieChartMarkers, setUsePieChartMarkers] = useState(false);
  
  // State for country sidebar
  const [selectedCountry, setSelectedCountry] = useState(null);

  // State for conference filter
  const [selectedConference, setSelectedConference] = useState('All');

  // Sponsor data state
  const [sponsorData, setSponsorData] = useState([]);
  const [sponsorDataLoading, setSponsorDataLoading] = useState(false);
  const [sponsorDataError, setSponsorDataError] = useState(null);

  // Load sponsor data on mount
  useEffect(() => {
    const loadData = async () => {
      setSponsorDataLoading(true);
      setSponsorDataError(null);
      try {
        const data = await loadSponsorData();
        setSponsorData(data);
        console.log('Sponsor data loaded in AppContext:', {
          recordCount: data.length,
          sampleRecord: data.length > 0 ? data[0] : null
        });
      } catch (error) {
        console.error('Failed to load sponsor data in AppContext:', error);
        setSponsorDataError(error.message || 'Failed to load sponsor data');
      } finally {
        setSponsorDataLoading(false);
      }
    };

    loadData();
  }, []);

  // Use custom hooks to manage application state
  const clientDataState = useClientData();
  const pinnedClientsState = usePinnedClients(clientDataState.filterPinnedClientsByYear);

  // Get pinned list state for filtering
  const pinnedListState = usePinnedListState(pinnedClientsState.pinnedClients, pinnedClientsState.pinnedViewType);

  const yearBoundaries = useMemo(() => buildYearBoundariesFromConfig(), []);

  /**
   * Extract unique conference names from sponsor data
   * @returns {Array<string>} Sorted array of unique conference names
   */
  const getUniqueConferences = useMemo(() => {
    if (!sponsorData || sponsorData.length === 0) {
      return [];
    }

    const conferences = new Set();
    sponsorData.forEach(record => {
      if (record.conferenceName && record.conferenceName.trim()) {
        conferences.add(record.conferenceName.trim());
      }
    });

    return Array.from(conferences).sort((a, b) => a.localeCompare(b));
  }, [sponsorData]);

  // Create a simplified filtering function for the map (without using the hook that depends on context)
  const filteredPinnedClients = useMemo(() => {
    let filteredClients = [...pinnedClientsState.orderedPinnedClients];

    // Apply conference filter first (if selected)
    if (selectedConference && selectedConference !== 'All') {
      const matchingCustomers = getConferenceMatchingClients(
        sponsorData,
        selectedConference,
        clientDataState.filters.selectedYear
      );
      filteredClients = filterClientsByConference(filteredClients, matchingCustomers);
    }

    // Enrich clients with conference-specific revenue
    filteredClients = enrichClientsWithConferenceRevenue(
      filteredClients,
      selectedConference,
      clientDataState.filters.selectedYear,
      sponsorData
    );

    // Apply tier filter
    if (pinnedListState.activeTierFilters.size > 0) {
      filteredClients = filteredClients.filter(client => {
        const clientTier = client['Tier'] || 'Unknown';
        return pinnedListState.activeTierFilters.has(clientTier);
      });
    }

    // Apply universal search filter
    if (pinnedListState.pinnedSearchQuery.trim()) {
      const query = pinnedListState.pinnedSearchQuery.toLowerCase().trim();
      filteredClients = filteredClients.filter(client => {
        // Search through all relevant fields
        const searchableFields = [
          client.client_name,
          client.country,
          client.conference_manager,
          client.group_size?.toString(),
          client['Tier'],
          client['Total Revenue'],
          client['Last Booking Revenue'],
          client['Last Booked'],
          client['Key Contact Name'],
          client['Key Contact Email'],
          client['Time zone'],
          client['Segment'],
          client['RN'],
          client['Previously Assigned Manager']
        ];

        return searchableFields.some(field =>
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Apply sorting
    if (pinnedListState.sortConfig.key !== null) {
      filteredClients.sort((a, b) => {
        const key = pinnedListState.sortConfig.key;
        const direction = pinnedListState.sortConfig.direction;

        // Handle special cases for revenue fields
        if (key === 'revenue') {
          const aValue = parseFloat((a['Total Revenue'] || '0').replace(/[$,]/g, '')) || 0;
          const bValue = parseFloat((b['Total Revenue'] || '0').replace(/[$,]/g, '')) || 0;
          return direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        // Handle tier sorting
        if (key === 'tier') {
          const aValue = parseInt(a['Tier']) || 0;
          const bValue = parseInt(b['Tier']) || 0;
          return direction === 'ascending' ? aValue - bValue : bValue - aValue;
        }

        // Special case: sort "group_size" (Total Rooms) by year-specific value as displayed
        if (key === 'group_size') {
          // Use getYearSpecificData from src/utils/yearSpecificUtils.js (imported at top)
          const aRooms = parseFloat(require('../utils/yearSpecificUtils').getYearSpecificData(a, clientDataState.filters.selectedYear).rooms) || 0;
          const bRooms = parseFloat(require('../utils/yearSpecificUtils').getYearSpecificData(b, clientDataState.filters.selectedYear).rooms) || 0;
          const diff = direction === 'ascending' ? aRooms - bRooms : bRooms - aRooms;
          if (diff === 0) {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          }
          return diff;
        }
        // Try to auto-detect numeric fields
        if (
          typeof a[key] !== 'undefined' &&
          typeof b[key] !== 'undefined' &&
          !isNaN(parseFloat(a[key])) &&
          !isNaN(parseFloat(b[key]))
        ) {
          const aValue = parseFloat(a[key]);
          const bValue = parseFloat(b[key]);
          const diff = direction === 'ascending' ? aValue - bValue : bValue - aValue;
          // Secondary sort by client name for stable ordering
          if (diff === 0) {
            const aName = (a.client_name || '').toLowerCase();
            const bName = (b.client_name || '').toLowerCase();
            return aName.localeCompare(bName);
          }
          return diff;
        }

        // Default: string comparison
        const aValue = (a[key] ?? '').toString().toLowerCase();
        const bValue = (b[key] ?? '').toString().toLowerCase();
        return direction === 'ascending'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    return filteredClients;
  }, [
    pinnedClientsState.orderedPinnedClients,
    pinnedListState.pinnedSearchQuery,
    pinnedListState.activeTierFilters,
    pinnedListState.sortConfig,
    selectedConference,
    sponsorData,
    clientDataState.filters.selectedYear
  ]);

  // Apply TopBar filters (Min Groups, Min Largest) to the pinned clients for the map
  const mapFilteredPinnedClients = useMemo(() => {
    if (filteredPinnedClients.length === 0) {
      return [];
    }

    // Group clients by country to apply min filters
    const countryGroups = {};
    filteredPinnedClients.forEach(client => {
      const country = client.country;
      if (!countryGroups[country]) {
        countryGroups[country] = [];
      }
      countryGroups[country].push(client);
    });

    // Apply min filters and flatten back to client list
    const filteredClients = [];
    Object.entries(countryGroups).forEach(([country, clients]) => {
      const totalGroups = clients.length;
      const largestGroup = Math.max(...clients.map(c => parseInt(c.group_size) || 0));

      // Only include clients from countries that meet the min criteria
      if (totalGroups >= clientDataState.filters.minTotal && largestGroup >= clientDataState.filters.minLargest) {
        filteredClients.push(...clients);
      }
    });

    return filteredClients;
  }, [filteredPinnedClients, clientDataState.filters.minTotal, clientDataState.filters.minLargest]);

  // Generate country data from filtered pinned clients for the map
  const pinnedCountryData = useMemo(() => {
    if (mapFilteredPinnedClients.length === 0) {
      return [];
    }
    const { countryData } = processCountryData(mapFilteredPinnedClients);
    return countryData;
  }, [mapFilteredPinnedClients]);

  // Helper functions for sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (pinnedListState.sortConfig.key === key && pinnedListState.sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    pinnedListState.setSortConfig({ key, direction });
  };

  const getSortDirectionIndicator = (key) => {
    if (pinnedListState.sortConfig.key !== key) return ' ⇅'; // Show sortable indicator
    return pinnedListState.sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Combine state from both hooks plus the new filtered data
  const contextValue = {
    ...clientDataState,
    ...pinnedClientsState,
    ...pinnedListState,
    // Add helper functions
    requestSort,
    getSortDirectionIndicator,
    // Add loading state
    isLoadingClients,
    setIsLoadingClients,
    // Add tier coloring state
    useTierColors,
    setUseTierColors,
    // Add pie chart marker state
    usePieChartMarkers,
    setUsePieChartMarkers,
    // Add country sidebar state
    selectedCountry,
    setSelectedCountry,
    // Add conference filter state
    selectedConference,
    setSelectedConference,
    // Override filtered data with pinned client data for map synchronization
    filteredClientData: mapFilteredPinnedClients,
    filteredCountryData: pinnedCountryData,
    // Keep original filtered data available if needed
    originalFilteredClientData: clientDataState.filteredClientData,
    originalFilteredCountryData: clientDataState.filteredCountryData,
    // Add sponsor data and related state
    sponsorData,
    sponsorDataLoading,
    sponsorDataError,
    conferences: getUniqueConferences,
    yearBoundaries,
    // Add conference sales data (from third sheet)
    conferenceSalesData: clientDataState.conferenceSalesData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

/**
 * Custom hook to use the app context
 * @returns {Object} App context
 */
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};

export default AppContext;
